import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import { fetchClient, $api } from "@/lib/api";
import type { LucideIcon } from "lucide-react";
import {
  Home,
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LogOut,
  BookOpen,
  Edit,
  Trash2,
  MoreVertical,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useProjectsQuery, projectKeys } from "@/hooks/useProjectsQuery";
import { useQueryClient } from "@tanstack/react-query";

interface Project {
  id: string;
  name: string;
}

interface LeftSidebarProps {
  currentProjectId?: string;
}

export default function LeftSidebar({ currentProjectId }: LeftSidebarProps) {
  const { user, backendToken, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [projectsExpanded, setProjectsExpanded] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredProjectId, setHoveredProjectId] = useState<string | null>(null);
  const [openMenuProjectId, setOpenMenuProjectId] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const { currentTheme, resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // Auto-collapse on mobile / small screens
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setMobileOpen(false);
      }
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (themeMenuRef.current && !themeMenuRef.current.contains(e.target as Node)) {
        setThemeMenuOpen(false);
      }
    };
    if (themeMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [themeMenuOpen]);

  const closeMobile = () => {
    if (isMobile) setMobileOpen(false);
  };

  const handleProjectClickWrapped = (projectId: string) => {
    navigate({ to: "/dashboard/projects/$projectId", params: { projectId } });
    closeMobile();
  };

  const handleProjectHover = (projectId: string) => {
    queryClient.prefetchQuery(
      $api.queryOptions("get", "/api/v1/projects/{project_id}", {
        params: { path: { project_id: projectId } },
      }),
    );
    queryClient.prefetchQuery(
      $api.queryOptions("get", "/api/v1/project-memory/{project_id}", {
        params: { path: { project_id: projectId } },
      }),
    );
    queryClient.prefetchQuery(
      $api.queryOptions("get", "/api/v1/projects/{project_id}/members", {
        params: { path: { project_id: projectId } },
      }),
    );
    queryClient.prefetchQuery(
      $api.queryOptions("get", "/api/v1/meetings/by-project/{project_id}", {
        params: { path: { project_id: projectId } },
      }),
    );
  };

  const handleSignOut = () => {
    logout();
  };

  // Load projects via cached hook
  const tokenForQuery = backendToken || undefined;
  const { data: projectsData = [], isFetching: loadingProjects } = useProjectsQuery({ accessToken: tokenForQuery });
  const projects = projectsData as Project[];

  const userName = user?.name || user?.email?.split('@')[0] || 'User';
  const userEmail = user?.email || '';
  const companyName = userEmail.split('@')[1] || 'Company';

  const handleProjectCreated = async () => {
    await queryClient.invalidateQueries({ queryKey: projectKeys.all });
  };

  return (
    <>
      {/* Mobile hamburger button */}
      {isMobile && !mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 rounded-lg shadow-md"
          style={{ backgroundColor: 'var(--surface-alt)', color: 'var(--text-primary)' }}
          aria-label="Open navigation menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Mobile backdrop */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 transition-opacity"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`flex flex-col transition-all duration-300 ${
          isMobile
            ? `fixed inset-y-0 left-0 z-50 w-[240px] shadow-xl ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`
            : isCollapsed ? 'w-16' : 'w-[240px]'
        }`}
        style={{ backgroundColor: 'var(--surface)' }}
      >
        {/* User Card */}
        <div className={`pt-5 pb-2 ${isCollapsed && !isMobile ? 'px-2' : 'px-4'}`}>
          <div
            className={`rounded-xl ${isCollapsed && !isMobile ? 'p-2 flex justify-center' : 'px-3 py-3'}`}
            style={{ backgroundColor: 'var(--surface-alt)' }}
          >
            <div className={`flex items-center ${isCollapsed && !isMobile ? 'justify-center' : 'gap-3'}`}>
              {(() => {
                const firstName = user?.name?.split(' ')[0] || '';
                const lastName = user?.name?.split(' ').slice(1).join(' ') || '';
                const initials = firstName && lastName
                  ? `${firstName[0]}${lastName[0]}`.toUpperCase()
                  : userName[0]?.toUpperCase() || 'U';
                return (
                  <div
                    className={`rounded-lg flex items-center justify-center flex-shrink-0 text-white font-semibold ${
                      isCollapsed && !isMobile ? 'w-8 h-8 text-sm cursor-pointer' : 'w-7 h-7 text-xs'
                    }`}
                    style={{ backgroundColor: 'var(--primary)' }}
                    onClick={() => { if (isCollapsed && !isMobile) setIsCollapsed(false); }}
                    title={isCollapsed ? `${userName} - Click to expand` : undefined}
                  >
                    {initials}
                  </div>
                );
              })()}
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{userName}</div>
                  <div className="text-xs truncate mt-0.5" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>{companyName}</div>
                </div>
              )}
              {(!isCollapsed || isMobile) && (
                <button
                  onClick={() => isMobile ? setMobileOpen(false) : setIsCollapsed(!isCollapsed)}
                  className="p-1 rounded-lg transition-colors flex-shrink-0"
                  style={{ color: 'var(--text-secondary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }}
                >
                  <ChevronLeft className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 overflow-auto mt-4">
          {/* Primary Navigation */}
          <div className="mb-2">
            <div className="space-y-1">
              <NavItem
                icon={Home}
                label="Home"
                to="/dashboard"
                collapsed={isCollapsed && !isMobile}
                onClick={closeMobile}
              />
              <NavItem
                icon={Calendar}
                label="Meetings"
                to="/dashboard/meetings"
                collapsed={isCollapsed && !isMobile}
                onClick={closeMobile}
              />
              <NavItem
                icon={BookOpen}
                label="Library"
                to="/dashboard/library"
                collapsed={isCollapsed && !isMobile}
                onClick={closeMobile}
              />
            </div>
          </div>

          {/* Engagements (Projects) Section */}
          {(!isCollapsed || isMobile) && (
            <div className="mb-4 mt-6">
              <div className="flex items-center justify-between px-3 mb-2">
                <button
                  onClick={() => setProjectsExpanded(!projectsExpanded)}
                  className="flex items-center gap-2 rounded-lg px-1 py-1 -ml-1 transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {projectsExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>Engagements</span>
                </button>
              </div>
              {projectsExpanded && (
                <div className="space-y-1">
                  {loadingProjects ? (
                    <div className="text-xs px-2 py-0.5" style={{ color: 'var(--text-primary)' }}>Loading...</div>
                  ) : projects.length === 0 ? (
                    <div className="text-xs px-2 py-0.5" style={{ color: 'var(--text-primary)' }}>No engagements</div>
                  ) : (
                    projects.map((project) => (
                      <ProjectItem
                        key={project.id}
                        label={project.name}
                        onClick={() => handleProjectClickWrapped(project.id)}
                        active={currentProjectId === project.id}
                        isHovered={hoveredProjectId === project.id}
                        onMouseEnter={() => {
                          setHoveredProjectId(project.id);
                          if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                          hoverTimerRef.current = setTimeout(() => handleProjectHover(project.id), 300);
                        }}
                        onMouseLeave={() => {
                          if (hoverTimerRef.current) {
                            clearTimeout(hoverTimerRef.current);
                            hoverTimerRef.current = null;
                          }
                          if (openMenuProjectId !== project.id) {
                            setHoveredProjectId(null);
                          }
                        }}
                        onMenuClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuProjectId(openMenuProjectId === project.id ? null : project.id);
                        }}
                        onEdit={() => {
                          navigate({ to: '/dashboard/new-project', search: { projectId: project.id } });
                          setOpenMenuProjectId(null);
                          setHoveredProjectId(null);
                        }}
                        onDelete={async () => {
                          if (confirm(`Are you sure you want to delete "${project.name}"?`)) {
                            try {
                              const { error: deleteError } = await fetchClient.DELETE("/api/v1/projects/{project_id}", {
                                params: { path: { project_id: project.id } },
                              });
                              if (deleteError) {
                                alert("Failed to delete project. Please try again.");
                              } else {
                                handleProjectCreated();
                              }
                            } catch (err) {
                              alert("Failed to delete project. Please try again.");
                            }
                          }
                          setOpenMenuProjectId(null);
                          setHoveredProjectId(null);
                        }}
                        menuOpen={openMenuProjectId === project.id}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="p-3">
          {/* Theme toggle */}
          <div className="relative" ref={themeMenuRef}>
            <button
              onClick={() => setThemeMenuOpen(!themeMenuOpen)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isCollapsed && !isMobile ? 'justify-center' : ''}`}
              title={isCollapsed && !isMobile ? 'Theme' : undefined}
              style={{ color: 'var(--text-secondary)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg)';
                e.currentTarget.style.color = 'var(--text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              {currentTheme.id === 'dark' ? (
                <Moon className="w-[18px] h-[18px]" />
              ) : currentTheme.id === 'system' ? (
                <Monitor className="w-[18px] h-[18px]" />
              ) : (
                <Sun className="w-[18px] h-[18px]" />
              )}
              {!(isCollapsed && !isMobile) && <span>Theme</span>}
            </button>
            {themeMenuOpen && (
              <div
                className="absolute left-0 bottom-full mb-1 rounded-lg shadow-lg z-50 py-1 min-w-[160px]"
                style={{ backgroundColor: 'var(--surface-alt)', border: '1px solid var(--border)' }}
              >
                {[
                  { id: 'light', label: 'Light', Icon: Sun },
                  { id: 'dark', label: 'Dark', Icon: Moon },
                  { id: 'system', label: 'System', Icon: Monitor },
                ].map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    onClick={() => { setTheme(id); setThemeMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors"
                    style={{
                      color: currentTheme.id === id ? 'var(--accent)' : 'var(--text-primary)',
                      backgroundColor: currentTheme.id === id ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (currentTheme.id !== id) e.currentTarget.style.backgroundColor = 'var(--bg)';
                    }}
                    onMouseLeave={(e) => {
                      if (currentTheme.id !== id) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleSignOut}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isCollapsed && !isMobile ? 'justify-center' : ''}`}
            style={{ color: 'var(--text-secondary)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            <LogOut className="w-[18px] h-[18px]" />
            {!isCollapsed && <span>Sign Out</span>}
          </button>

          {/* Centring Logo */}
          <div className={`flex items-center justify-center mt-3 ${isCollapsed ? 'px-1 py-1' : 'px-3 py-2'}`}>
            {isCollapsed ? (
              <img
                src={isDark ? "/dark_mode/Light_Blue_Transparent.png" : "/Centring_logo.png"}
                alt="Centring"
                className="w-8 h-8 object-contain"
              />
            ) : (
              <img
                src={isDark ? "/dark_mode/Light_Blue_Transparent_Horizontal.png" : "/Centring_horizontal.png"}
                alt="Centring"
                className="w-full max-w-[140px] object-contain"
              />
            )}
          </div>
        </div>
      </aside>


    </>
  );
}

function NavItem({
  icon: Icon,
  label,
  to,
  collapsed = false,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  to: string;
  collapsed?: boolean;
  onClick?: () => void;
}) {
  const { pathname } = useLocation();
  const active = to === "/dashboard"
    ? pathname === "/dashboard" || pathname === "/dashboard/"
    : pathname.startsWith(to);

  return (
    <Link
      to={to}
      onClick={onClick}
      preload="intent"
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${collapsed ? 'justify-center' : ''}`}
      title={collapsed ? label : undefined}
      style={{
        backgroundColor: active ? 'var(--surface-alt)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = 'var(--bg)';
          e.currentTarget.style.color = 'var(--text-primary)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'var(--text-secondary)';
        }
      }}
    >
      <Icon className="w-[18px] h-[18px]" />
      {!collapsed && <span>{label}</span>}
    </Link>
  );
}

function ProjectItem({
  label,
  onClick,
  active,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onMenuClick,
  onEdit,
  onDelete,
  menuOpen
}: {
  label: string;
  onClick?: () => void;
  active?: boolean;
  isHovered?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onMenuClick?: (e: React.MouseEvent) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  menuOpen?: boolean;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        if (menuOpen && onMouseLeave) {
          onMouseLeave();
        }
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen, onMouseLeave]);

  const initial = label.trim()[0]?.toUpperCase() || '?';

  return (
    <div
      className="relative group"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer"
        style={{
          backgroundColor: active ? 'var(--sidebar-accent)' : 'transparent',
          color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.backgroundColor = 'var(--sidebar-accent)';
            e.currentTarget.style.color = 'var(--text-primary)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--text-secondary)';
          }
        }}
      >
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 text-[11px] font-semibold"
          style={{
            backgroundColor: active ? 'var(--accent)' : 'var(--bg)',
            color: active ? 'white' : 'var(--text-secondary)',
          }}
        >
          {initial}
        </div>
        <span className="flex-1 text-left truncate">{label}</span>
        <button
          onClick={onMenuClick}
          className={`flex-shrink-0 p-1 rounded transition-opacity ${
            isHovered || menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ backgroundColor: 'transparent' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--surface-alt)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <MoreVertical className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
        </button>
      </div>

      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 top-full mt-1 rounded-lg shadow-lg z-50 py-1 min-w-[160px]"
          style={{
            backgroundColor: 'var(--surface-alt)',
            border: '1px solid var(--border)'
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onEdit) onEdit();
            }}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors"
            style={{ color: 'var(--text-primary)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onDelete) onDelete();
            }}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors"
            style={{ color: 'var(--error)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
